/* eslint-disable no-console */
// Error boundary with development logging
import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw, Home } from '@/components/ui/icon';
import i18n from '@/i18n/config';
import { captureConsentedException } from '@/lib/sentry-loader';

interface Props {
  children: ReactNode;
  fallbackMessage?: string;
  onReset?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  private titleRef = React.createRef<HTMLHeadingElement>();

  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (import.meta.env.DEV) {
      console.error('Error Boundary caught an error:', error, errorInfo);
    }

    void captureConsentedException(error, {
      extra: { componentStack: errorInfo.componentStack },
      level: 'error',
    });

    this.setState(
      {
        error,
        errorInfo,
      },
      () => {
        this.titleRef.current?.focus();
      },
    );
  }

  private resetErrorBoundary = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });

    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  private handleGoHome = () => {
    window.location.href = '/dashboard';
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-dvh items-center justify-center bg-gradient-to-br from-background via-secondary/20 to-background px-4 py-6 pb-[var(--app-mobile-dock-clearance)] md:pb-6">
          <Card className="surface-card no-hover w-full max-w-2xl border-0">
            <CardHeader>
              <div className="mb-2 flex items-center gap-3">
                <div className="rounded-full bg-destructive/10 p-3">
                  <AlertTriangle className="h-6 w-6 text-destructive" aria-hidden="true" />
                </div>
                <div>
                  <CardTitle ref={this.titleRef} tabIndex={-1} className="text-h2 outline-none">
                    {i18n.t('errors.somethingWentWrong')}
                  </CardTitle>
                  <CardDescription className="mt-1">
                    {this.props.fallbackMessage || i18n.t('errors.unexpectedError')}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {import.meta.env.DEV && this.state.error && (
                <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-4">
                  <p className="mb-2 font-mono text-body text-destructive">
                    {this.state.error.toString()}
                  </p>
                  {this.state.errorInfo && (
                    <details className="mt-2">
                      <summary className="cursor-pointer text-meta text-muted-foreground hover:text-foreground">
                        {i18n.t('errors.stackTrace')}
                      </summary>
                      <pre className="mt-2 max-h-64 overflow-auto rounded bg-background/50 p-2 text-meta">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </details>
                  )}
                </div>
              )}

              <div className="flex flex-col gap-3 sm:flex-row">
                <Button onClick={this.resetErrorBoundary} className="flex-1 gap-2">
                  <RefreshCw data-icon="inline-start" className="h-4 w-4" aria-hidden="true" />
                  {i18n.t('errors.tryAgain')}
                </Button>
                <Button variant="outline" onClick={this.handleGoHome} className="flex-1 gap-2">
                  <Home data-icon="inline-start" className="h-4 w-4" aria-hidden="true" />
                  {i18n.t('errors.goToDashboard')}
                </Button>
              </div>

              <p className="text-center text-meta text-muted-foreground">
                {i18n.t('errors.contactSupport')}
              </p>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

// Hook-based error boundary wrapper for functional components
// eslint-disable-next-line react-refresh/only-export-components
export const withErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>,
  fallbackMessage?: string,
) => {
  return (props: P) => (
    <ErrorBoundary fallbackMessage={fallbackMessage}>
      <Component {...props} />
    </ErrorBoundary>
  );
};

// Route-specific error boundary with route name in error message
interface RouteErrorBoundaryProps {
  children: ReactNode;
  routeName?: string;
}

export const RouteErrorBoundary: React.FC<RouteErrorBoundaryProps> = ({ children, routeName }) => {
  return (
    <ErrorBoundary
      fallbackMessage={routeName ? i18n.t('errors.routeError', { routeName }) : undefined}
      onReset={() => {
        // Clear any cached route data
        window.location.reload();
      }}
    >
      {children}
    </ErrorBoundary>
  );
};
